// src/components/TickerModal.tsx
import { useEffect, useRef, useState } from 'react'
import type { Category, Ticker } from '../types.ts'

interface Props {
  ticker?: Ticker        // undefined = criar novo; definido = editar
  categories: Category[]
  userId: number
  onClose: () => void
  onSave: (data: {
    ticker: string
    cnpj: string | null
    qtd: string | null
    preco_medio: string | null
    category_id: number | null
  }) => void
  isSaving: boolean
}

function buildCategoryPath(category: Category, byId: Map<number, Category>): string {
  const parts = [category.name]
  let current = category

  while (current.parent_id) {
    const parent = byId.get(current.parent_id)
    if (!parent) break
    parts.unshift(parent.name)
    current = parent
  }

  return parts.join(' / ')
}

export function TickerModal({ ticker, categories, userId: _userId, onClose, onSave, isSaving }: Props) {
  const [tickerVal, setTickerVal] = useState(ticker?.ticker ?? '')
  const [cnpjVal, setCnpjVal] = useState(ticker?.cnpj ?? '')
  const [qtdVal, setQtdVal] = useState(ticker?.qtd ?? '')
  const [precoMedioVal, setPrecoMedioVal] = useState(ticker?.preco_medio ?? '')
  const [categoryIdVal, setCategoryIdVal] = useState<string>(
    ticker?.category_id ? String(ticker.category_id) : '',
  )
  const inputRef = useRef<HTMLInputElement>(null)

  // Fechar com Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [onClose])

  // Focar no primeiro campo ao abrir
  useEffect(() => { inputRef.current?.focus() }, [])

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!tickerVal.trim()) return
    const normalizeDecimalInput = (value: string) => value.trim().replace(',', '.')
    onSave({
      ticker: tickerVal.trim().toUpperCase(),
      cnpj: cnpjVal.trim() || null,
      qtd: qtdVal.trim() ? normalizeDecimalInput(qtdVal) : null,
      preco_medio: precoMedioVal.trim() ? normalizeDecimalInput(precoMedioVal) : null,
      category_id: categoryIdVal ? Number(categoryIdVal) : null,
    })
  }

  const categoriesById = new Map(categories.map((category) => [category.id, category]))

  return (
    <div className="modal-overlay">
      <div className="modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{ticker ? 'Editar Ticker' : 'Novo Ticker'}</h3>
          <button className="modal-close" onClick={onClose} aria-label="Fechar">✕</button>
        </div>

        <form onSubmit={handleSubmit} className="modal-body">
          <div className="field">
            <label htmlFor="ticker-input">Ticker *</label>
            <input
              id="ticker-input"
              ref={inputRef}
              type="text"
              value={tickerVal}
              onChange={(e) => setTickerVal(e.target.value)}
              placeholder="Ex: KNRI11"
              maxLength={10}
              required
            />
          </div>

          <div className="field">
            <label htmlFor="cnpj-input">CNPJ</label>
            <input
              id="cnpj-input"
              type="text"
              value={cnpjVal}
              onChange={(e) => setCnpjVal(e.target.value)}
              placeholder="00.000.000/0001-00"
              maxLength={18}
            />
          </div>

          <div className="field">
            <label htmlFor="qtd-input">Qtd</label>
            <input
              id="qtd-input"
              type="text"
              value={qtdVal}
              onChange={(e) => setQtdVal(e.target.value)}
              placeholder="Ex: 100.5"
            />
          </div>

          <div className="field">
            <label htmlFor="preco-medio-input">Preco medio</label>
            <input
              id="preco-medio-input"
              type="text"
              value={precoMedioVal}
              onChange={(e) => setPrecoMedioVal(e.target.value)}
              placeholder="Ex: 97.35"
            />
          </div>

          <div className="field">
            <label htmlFor="ticker-category-input">Categoria</label>
            <select
              id="ticker-category-input"
              value={categoryIdVal}
              onChange={(e) => setCategoryIdVal(e.target.value)}
            >
              <option value="">(sem categoria)</option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {buildCategoryPath(category, categoriesById)}
                </option>
              ))}
            </select>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn--ghost" onClick={onClose}>
              Cancelar
            </button>
            <button type="submit" className="btn btn--primary" disabled={isSaving}>
              {isSaving ? 'Salvando…' : 'Salvar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
